import { Logger } from '@frontend/logger';
import { PackageStateName, PackageType } from '@frontend/package/types';
import { StockExtended } from '@frontend/stock/types';
import { TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionStateName, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import { useState } from 'react';

import { useWizardPackages } from '../../hooks/useWizardPackages';
import { useWizardSettings } from '../../hooks/useWizardSettings';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';
import type { LendingPickByStateProps } from './lending-pick-by-state.component';

export type LendingPickByStateSetupError = 'missing_configuration' | 'setup_failed';

interface ViewProps {
    setupError: LendingPickByStateSetupError | null;
    processing: boolean;
    onBack: () => void;
    onSubmit: (stocks: StockExtended[]) => Promise<void>;
}

const useLendingPickByState = (props: LendingPickByStateProps): ViewProps => {
    const { navigationState, store, wizardId, onBack, onSubmit } = useScreen();
    const { transactionWorkflowId, pickUpPackageWorkflowId } = useWizardSettings(store);
    const transactionContext = useWizardTransaction({
        wizardId,
        store,
        autoFetch: false
    });
    const { setPackages, syncPackages } = useWizardPackages({
        wizardId,
        store,
        transactionContext,
        autoFetch: false
    });

    const [processing, setProcessing] = useState(false);
    const [setupError, setSetupError] = useState<LendingPickByStateSetupError | null>(null);

    const handleSubmit = async (stocks: StockExtended[]) => {
        if (!store?.functionality || !transactionWorkflowId || !pickUpPackageWorkflowId) {
            Logger.error('Missing workflow configuration for lending pick by state transaction');
            setSetupError('missing_configuration');
            return;
        }

        try {
            setProcessing(true);
            setSetupError(null);

            const transaction = await transactionContext.syncTransaction({
                application: store.functionality.application,
                feature: store.functionality.feature,
                workflow_id: transactionWorkflowId,
                type: TransactionType.LENDING,
                state: TransactionStateName.TRANSACTION_CREATED,
                user_id: navigationState.user?.id || undefined,
                iot_id: navigationState.iot?.id || undefined
            });

            await setPackages(
                stocks.map((stock, index) => ({
                    workflow_id: pickUpPackageWorkflowId,
                    user_id: transaction.user_id,
                    transaction_id: transaction.id,
                    account_id: transaction.account_id,
                    quantity: 1,
                    type: PackageType.PICK_UP,
                    state: PackageStateName.PACKAGE_CREATED,
                    spot_id: stock.spot_id,
                    module_id: stock.module_id,
                    slot_id: stock.slot_id,
                    product_id: stock.product_id,
                    equipment_id: stock.equipment_id,
                    priority: index + 1,
                    blocking: false,
                    application: store.functionality.application,
                    feature: store.functionality.feature
                }))
            );

            await syncPackages();

            await TransactionWorkflowClient.triggerTransactionState(transaction.account_id, transaction.id, {
                trigger: TransactionTriggerName.TRANSACTION_PROCESS,
                location: navigationState.spot?.id ?? undefined,
                actor: navigationState.user?.id ?? undefined
            });

            onSubmit();
        } catch (error) {
            Logger.error('Failed to initialise lending pick by state transaction', error);
            setSetupError('setup_failed');
        } finally {
            setProcessing(false);
        }
    };

    return {
        setupError,
        processing,
        onBack,
        onSubmit: handleSubmit
    };
};

export default useLendingPickByState;
